/**
 * The start-line dispersion chart (components/StartLineBarChart.tsx) as data:
 * seven buckets left to right (misses outside, PURE in the middle), each with
 * its label, tone token, count, and bar height. Pure; shared by the phone
 * chart and both TV receivers so the bins, labels, and scale cannot drift.
 * Moved out of StartLineBarChart unchanged.
 */
import {
  classifyStartLine,
  startLineCategoryChartLabel,
  startLineCategoryToneKey,
  type StartLineCategory,
} from '../utils/startLine';

export const START_LINE_CHART_ORDER: readonly StartLineCategory[] = [
  'missed_left',
  'off_left',
  'good_left',
  'pure',
  'good_right',
  'off_right',
  'missed_right',
];

export type StartLineChartToneKey = ReturnType<typeof startLineCategoryToneKey>;

export interface StartLineChartBar {
  category: StartLineCategory;
  /** Upper-case chart label, e.g. "<-- WIDE". */
  label: string;
  /** Two-line label for the compact phone-portrait chart. */
  compactLabel: string;
  toneKey: StartLineChartToneKey;
  count: number;
  /** Bar height as a fraction of the track (0 for an empty bucket). */
  heightFraction: number;
}

export interface StartLineChartModel {
  bars: StartLineChartBar[];
  /** Putts with a usable angle. 0 shows "No start-line data captured." */
  classified: number;
  maxCount: number;
}

function compactChartLabel(category: StartLineCategory, label: string): string {
  const words = label.split(' ');
  if (category === 'pure') return words.join('\n');
  // Category name on line one and its direction directly below it.
  return category.endsWith('_left')
    ? `${words.slice(1).join(' ')}\n${words[0]}`
    : `${words.slice(0, -1).join(' ')}\n${words[words.length - 1]}`;
}

export function startLineChartModel(
  angles: readonly (number | null)[],
  scaleMaxCount?: number,
): StartLineChartModel {
  const counts = new Map<StartLineCategory, number>(START_LINE_CHART_ORDER.map((c) => [c, 0]));
  let classified = 0;
  for (const a of angles) {
    if (a === null || !Number.isFinite(a)) continue;
    const category = classifyStartLine(a);
    counts.set(category, (counts.get(category) ?? 0) + 1);
    classified++;
  }
  const maxCount = Math.max(
    1,
    ...START_LINE_CHART_ORDER.map((k) => counts.get(k) ?? 0),
    Number.isFinite(scaleMaxCount) ? (scaleMaxCount as number) : 1,
  );
  const bars = START_LINE_CHART_ORDER.map((category) => {
    const label = startLineCategoryChartLabel(category).toUpperCase();
    const count = counts.get(category) ?? 0;
    return {
      category,
      label,
      compactLabel: compactChartLabel(category, label),
      toneKey: startLineCategoryToneKey(category),
      count,
      heightFraction: count === 0 ? 0 : count / maxCount,
    };
  });
  return { bars, classified, maxCount };
}
